<template>
  <main class="main-content">
    <!-- 欢迎区域 -->
    <section class="hero">
      <h1>身份验证</h1>
      <p>
        了解如何安全地调用银企通平台API，包括认证机制、请求头设置和安全凭证生成
      </p>
    </section>

    <!-- 认证概述 -->
    <section class="section">
      <div class="section-header">
        <h2>认证概述</h2>
        <p>
          调用银企通平台API时，需要在HTTP
          Header中携带访问凭证，以便获取权限范围内的资源信息
        </p>
      </div>

      <div class="auth-flow">
        <div class="auth-step">
          <div class="auth-step-number">1</div>
          <div class="auth-step-content">
            <h3>获取AppID和AppSecret</h3>
            <p>
              在银企通平台中创建应用后，您将获得唯一的AppID和AppSecret，用于API身份验证
            </p>
            <div class="note">
              <strong>注意：</strong
              >请妥善保管您的AppSecret，不要将其暴露在客户端代码中
            </div>
          </div>
        </div>

        <div class="auth-step">
          <div class="auth-step-number">2</div>
          <div class="auth-step-content">
            <h3>生成securityCode</h3>
            <p>使用AppSecret和当前日期通过MD5算法生成每日的安全码</p>
            <div class="api-details">
              <pre>
// securityCode生成算法
MD5([AppSecret]#[当前日期YYYYMMDD])</pre
              >
            </div>
          </div>
        </div>

        <div class="auth-step">
          <div class="auth-step-number">3</div>
          <div class="auth-step-content">
            <h3>设置请求头</h3>
            <p>在API请求的Header中设置x-xencio-client-id和securityCode参数</p>
            <table class="header-table">
              <thead>
                <tr>
                  <th>Header字段</th>
                  <th>描述</th>
                  <th>示例值</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>x-xencio-client-id</td>
                  <td>应用的唯一标识符</td>
                  <td>4d9414e89bc24b0d89b678d9f20bc56a</td>
                </tr>
                <tr>
                  <td>securityCode</td>
                  <td>基于AppSecret和日期生成的认证码</td>
                  <td>8201e508ef948b8fcdca855729e7ee34</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- 沙盒环境 -->
    <section class="section">
      <div class="section-header">
        <h2>沙盒环境</h2>
        <p>测试联调请统一使用下面沙盒环境的凭证：</p>
      </div>

      <div class="api-details">
        <pre>
Server: https://openapi.xencio.com/sandbox/cfa/api
AppID: 4d9414e89bc24b0d89b678d9f20bc56a
AppSecret: KHPuj0JMS6nS1ACzc</pre
        >
      </div>

      <div class="note">
        <strong>注意：</strong
        >因部署方式可能存在SaaS和本地化，具体生产环境的API地址请联系客服。
      </div>
    </section>

    <!-- 加密方式 -->
    <section class="section">
      <div class="section-header">
        <h2>加密方式说明</h2>
        <p>调用OpenAPI开放的标准接口时，需要在HTTP Header中携带访问凭证</p>
      </div>

      <div class="api-details">
        <pre>
// securityCode生成示例
假设：
  AppSecret = "KHPuj0JMS6nS1ACzc"
  当前日期 = 20240512

计算过程：
  securityCode = MD5("KHPuj0JMS6nS1ACzc#20240512")
              = 8201E508EF948B8FCDCA855729E7EE34

注意：securityCode需要转换为小写后使用</pre
        >
      </div>

      <div class="code-generator">
        <h4>securityCode生成器</h4>
        <div class="generator-form">
          <div class="form-group">
            <label for="appSecret">AppSecret</label>
            <input
              type="text"
              id="appSecret"
              placeholder="输入您的AppSecret"
              v-model="appSecret"
            />
          </div>
          <div class="form-group">
            <label for="currentDate">日期 (YYYYMMDD)</label>
            <input
              type="text"
              id="currentDate"
              placeholder="输入日期，如：20240512"
              v-model="currentDate"
            />
          </div>
        </div>
        <button class="btn btn-primary" @click="generateSecurityCode">
          生成securityCode
        </button>

        <div class="generator-result" id="codeResult" v-if="securityCode">
          <pre>{{ securityCode }}</pre>
        </div>
      </div>
    </section>

    <!-- 请求头说明 -->
    <section class="section">
      <div class="section-header">
        <h2>请求头说明</h2>
        <p>调用OpenAPI开放的标准接口时，需要在HTTP Header中设置正确的参数</p>
      </div>

      <table class="param-table">
        <thead>
          <tr>
            <th>参数名</th>
            <th>类型</th>
            <th>是否必填</th>
            <th>描述</th>
            <th>示例</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>x-xencio-client-id</td>
            <td>String</td>
            <td>是</td>
            <td>使用AppID值</td>
            <td>4d9414e89bc24b0d89b678d9f20bc56a（沙盒环境）</td>
          </tr>
          <tr>
            <td>securityCode</td>
            <td>String</td>
            <td>是</td>
            <td>通过AppSecret摘要算法生成访问凭证</td>
            <td>8201e508ef948b8fcdca855729e7ee34</td>
          </tr>
        </tbody>
      </table>

      <div class="note">
        <strong>注意：</strong>请求头content-type说明：<br />
        - 通用标准接口：content-type:application/x-www-form-urlencoded<br />
        - 文件上传接口：content-type:multipart/form-data
      </div>
    </section>

    <!-- 代码示例 -->
    <section class="section">
      <div class="section-header">
        <h2>代码示例</h2>
        <p>以下是在不同编程语言中实现身份验证的示例</p>
      </div>

      <div class="api-item">
        <div class="api-item-header">
          <span class="api-method method-post">POST</span>
          <span class="api-url">/api/example</span>
        </div>
        <div class="api-description">示例API请求，展示如何设置认证头</div>

        <h4>JavaScript示例</h4>
        <div class="api-details">
          <pre>
const CryptoJS = require("crypto-js");

// 生成securityCode
function generateSecurityCode(appSecret) {
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth()+1).padStart(2,'0')}${String(today.getDate()).padStart(2,'0')}`;
    return CryptoJS.MD5(`${appSecret}#${dateStr}`).toString().toLowerCase();
}

// API请求示例
const appId = '4d9414e89bc24b0d89b678d9f20bc56a';
const appSecret = 'KHPuj0JMS6nS1ACzc';
const securityCode = generateSecurityCode(appSecret);

// 设置请求头
const headers = {
    'x-xencio-client-id': appId,
    'securityCode': securityCode,
    'Content-Type': 'application/x-www-form-urlencoded'
};

// 发起请求
fetch('https://openapi.xencio.com/sandbox/cfa/api/example', {
    method: 'POST',
    headers: headers,
    body: 'param1=value1&param2=value2'
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));</pre
          >
        </div>

        <h4>Python示例</h4>
        <div class="api-details">
          <pre>
import hashlib
import datetime
import requests

# 生成securityCode
def generate_security_code(app_secret):
    today = datetime.date.today()
    date_str = today.strftime("%Y%m%d")
    input_str = f"{app_secret}#{date_str}"
    return hashlib.md5(input_str.encode()).hexdigest().lower()

# API请求示例
app_id = '4d9414e89bc24b0d89b678d9f20bc56a'
app_secret = 'KHPuj0JMS6nS1ACzc'
security_code = generate_security_code(app_secret)

# 设置请求头
headers = {
    'x-xencio-client-id': app_id,
    'securityCode': security_code,
    'Content-Type': 'application/x-www-form-urlencoded'
}

# 发起请求
data = {'param1': 'value1', 'param2': 'value2'}
response = requests.post(
    'https://openapi.xencio.com/sandbox/cfa/api/example',
    headers=headers,
    data=data
)

print(response.json())</pre
          >
        </div>

        <h4>Java示例</h4>
        <div class="api-details">
          <pre>
import java.security.MessageDigest;
import java.security.NoSuchAlgorithmException;
import java.time.LocalDate;
import java.time.format.DateTimeFormatter;

public class ApiAuthExample {
    
    // 生成securityCode
    public static String generateSecurityCode(String appSecret) {
        LocalDate today = LocalDate.now();
        String dateStr = today.format(DateTimeFormatter.ofPattern("yyyyMMdd"));
        String input = appSecret + "#" + dateStr;
        
        try {
            MessageDigest md = MessageDigest.getInstance("MD5");
            byte[] digest = md.digest(input.getBytes());
            StringBuilder sb = new StringBuilder();
            for (byte b : digest) {
                sb.append(String.format("%02x", b));
            }
            return sb.toString().toLowerCase();
        } catch (NoSuchAlgorithmException e) {
            throw new RuntimeException("MD5 algorithm not found", e);
        }
    }
    
    public static void main(String[] args) {
        String appId = "4d9414e89bc24b0d89b678d9f20bc56a";
        String appSecret = "KHPuj0JMS6nS1ACzc";
        String securityCode = generateSecurityCode(appSecret);
        
        // 使用HttpClient或其他HTTP库设置请求头
        // headers.put("x-xencio-client-id", appId);
        // headers.put("securityCode", securityCode);
        // headers.put("Content-Type", "application/x-www-form-urlencoded");
    }
}</pre
          >
        </div>
      </div>
    </section>

    <!-- 常见问题 -->
    <section class="section">
      <div class="section-header">
        <h2>常见问题</h2>
        <p>身份验证过程中可能遇到的问题和解决方案</p>
      </div>

      <div class="api-item">
        <h4>1. 认证失败，错误代码：401</h4>
        <p>可能原因：</p>
        <ul>
          <li>AppID或AppSecret不正确</li>
          <li>securityCode计算错误（日期格式不正确或未转换为小写）</li>
          <li>服务器时间与北京时间不一致</li>
        </ul>
        <p>
          解决方案：检查AppID和AppSecret是否正确，验证securityCode生成算法，确保服务器时间正确。
        </p>

        <h4>2. 认证失败，错误代码：403</h4>
        <p>可能原因：</p>
        <ul>
          <li>应用没有调用该API的权限</li>
          <li>IP地址不在白名单中</li>
        </ul>
        <p>解决方案：在银企通平台中检查应用权限设置和IP白名单配置。</p>

        <h4>3. securityCode每日需要更新</h4>
        <p>securityCode基于当前日期生成，每日凌晨会自动失效，需要重新生成。</p>
        <p>
          解决方案：确保每次API调用都使用当天的securityCode，不要缓存过期的认证信息。
        </p>
      </div>
    </section>

    <!-- 下一步 -->
    <section class="section">
      <div class="section-header">
        <h2>下一步</h2>
        <p>完成身份验证学习后，您可以继续探索更多功能</p>
      </div>

      <div class="card-grid">
        <div class="card">
          <div class="card-icon">
            <i class="fas fa-book"></i>
          </div>
          <h3>查看API文档</h3>
          <p>详细了解每个API接口的参数、返回值和调用示例。</p>
          <a href="#" class="card-link"
            >查看文档 <i class="fas fa-arrow-right"></i
          ></a>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fas fa-cogs"></i>
          </div>
          <h3>测试API</h3>
          <p>使用在线测试工具验证您的身份验证配置。</p>
          <a href="#" class="card-link"
            >开始测试 <i class="fas fa-arrow-right"></i
          ></a>
        </div>

        <div class="card">
          <div class="card-icon">
            <i class="fas fa-headset"></i>
          </div>
          <h3>获取帮助</h3>
          <p>联系技术支持解决认证相关问题。</p>
          <a href="#" class="card-link"
            >联系支持 <i class="fas fa-arrow-right"></i
          ></a>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { md5 } from "crypto-js";
import { useCodeHighlight } from "@/composables/useCodeHighlight";

// 定义响应式数据
const appSecret = ref("KHPuj0JMS6nS1ACzc");
const currentDate = ref("");
const securityCode = ref("");

// 设置当前日期的默认值
onMounted(() => {
  const today = new Date();
  const dateStr = `${today.getFullYear()}${String(
    today.getMonth() + 1
  ).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
  currentDate.value = dateStr;
});

// 使用代码高亮功能
useCodeHighlight(".api-details pre");

// 生成securityCode
const generateSecurityCode = () => {
  if (!appSecret.value || !currentDate.value) {
    alert("请填写AppSecret和日期");
    return;
  }

  // 使用CryptoJS计算MD5
  securityCode.value = md5(`${appSecret.value}#${currentDate.value}`)
    .toString()
    .toLowerCase();
};
</script>

<style scoped>
.hero {
  background: linear-gradient(120deg, #7486f1, #955bea);
  border-radius: 12px;
  padding: 2.5rem;
  color: white;
  margin-bottom: 2rem;
}

.hero h1 {
  font-size: 2.25rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.hero p {
  font-size: 1.1rem;
  max-width: 700px;
  margin-bottom: 1.5rem;
  opacity: 0.9;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.card {
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s, box-shadow 0.2s;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.card h3 {
  font-size: 1.1rem;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
}

.card p {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.card-link {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.card-link i {
  margin-left: 0.5rem;
  transition: transform 0.2s;
}

.card-link:hover i {
  transform: translateX(3px);
}

.section {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 1.5rem;
}

.section-header h2 {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.section-header p {
  color: var(--text-secondary);
}

.api-reference {
  background-color: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.api-item {
  border-bottom: 1px solid var(--border);
  padding: 1.5rem;
}

.api-item:last-child {
  border-bottom: none;
}

.api-item-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.api-method {
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.75rem;
}

.method-get {
  background-color: rgba(71, 184, 129, 0.1);
  color: #47b881;
}

.method-post {
  background-color: rgba(58, 109, 248, 0.1);
  color: var(--primary);
}

.api-url {
  font-size: 0.9rem;
  color: var(--text-primary);
}

.api-description {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-bottom: 1rem;
}

.api-details {
  background-color: var(--code-bg);
  color: var(--code-color);
  border-radius: 8px;
  padding: 1rem;
  font-size: 0.85rem;
  overflow-x: auto;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.api-details pre {
  margin: 0;
  white-space: pre-wrap;
  background-color: #000 !important;
  color: #fff !important;
  padding: 1rem;
  border-radius: 4px;
}

.try-section {
  background-color: var(--secondary);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.try-section h4 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.9rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.9rem;
}

.response-area {
  margin-top: 1.5rem;
  background-color: var(--code-bg);
  border-radius: 8px;
  padding: 1rem;
  color: var(--code-color);
  font-size: 0.85rem;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
}

.response-area pre {
  margin: 0;
  white-space: pre-wrap;
}

.param-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.param-table th,
.param-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.param-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

/* 身份验证特定样式 */
.auth-flow {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem 0;
}

.auth-step {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
}

.auth-step-number {
  background: var(--primary);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  flex-shrink: 0;
}

.auth-step-content {
  flex: 1;
}

.auth-step-content h3 {
  margin-bottom: 0.5rem;
  color: var(--text-primary);
}

.auth-step-content p {
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

.header-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1rem 0;
}

.header-table th,
.header-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--border);
}

.header-table th {
  background-color: var(--secondary);
  font-weight: 600;
}

.code-generator {
  background-color: var(--secondary);
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
}

.code-generator h4 {
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.generator-form {
  margin-bottom: 1rem;
}

.generator-result {
  margin-top: 1.5rem;
  background-color: var(--code-bg);
  border-radius: 8px;
  padding: 1rem;
  color: var(--code-color);
  font-size: 0.85rem;
  overflow-x: auto;
}

.generator-result pre {
  margin: 0;
  white-space: pre-wrap;
}

.note {
  background: rgba(255, 193, 7, 0.1);
  border-left: 4px solid #ffc107;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
}

</style>
